import Button from 'components/Button';
import MentorshipStatusChip from 'components/Chips/MentorshipStatusChip';
import getFilters from 'components/Filters';
import List from 'components/list';
import { BoldTd, Td } from 'components/list/Td';
import { getOrderTh, Th } from 'components/list/Th';
import React, { FC, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { setOrder, setPage, setSearch } from 'store/actions/mentor/menteeLists';
import { StoreState } from 'store/reducers';
import styled from 'styled-components';
import useSWR from 'swr';
import GetOrderValue from 'types/GetOrderValue';
import Mentorship from 'types/Mentorship';
import { Action } from 'types/typeguards/reducerActions';
import { getLocaleDate } from 'utils/time';

const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  column-gap: 1rem;
`;

// const ViewProfileLink = styled(Link)`
//   color: var(--primary-700);
//   font-weight: 500;
// `;

export const columnNames = [
  'firstName',
  'lastName',
  'region',
  'status',
  'createdAt',
  'updatedAt',
  'viewProfile',
] as const;
export type ColumnNames = typeof columnNames[number];
export type Columns = {
  [key in ColumnNames]: boolean;
};

export const sortableColumns = [
  'matching',
  'firstName',
  'lastName',
  'createdAt',
  'updatedAt',
  'status',
] as const;
export type OrderValue = GetOrderValue<typeof sortableColumns[number]>;
const OrderTh = getOrderTh<typeof sortableColumns[number]>();

export const activeColumns: Columns = {
  firstName: true,
  lastName: true,
  region: true,
  status: true,
  createdAt: true,
  updatedAt: true,
  viewProfile: true,
};

type RetrievedData = {
  mentorships: Mentorship[];
  count: number;
  pages: number;
};

const defaultData = {
  mentorships: undefined,
  count: undefined,
  pages: undefined,
};

type Props = {
  page: number;
  setPage: (page: number) => void;
  search: string;
  setSearch: (search: string) => void;
  order: OrderValue;
  setOrder: (order: OrderValue) => void;
};

const Filters = getFilters();

const HistoryList: FC<Props> = ({
  page,
  setPage,
  search,
  setSearch,
  order,
  setOrder,
}) => {
  const { data, error } = useSWR<RetrievedData>(
    `/mentorship/mentor/history/?page=${page}&take=30${
      search ? `&search=${search}` : ''
    }&order=${order}`,
  );
  const [retrievedData, setRetrievedData] =
    useState<Partial<RetrievedData>>(defaultData);

  useEffect(() => {
    if (data) {
      setRetrievedData(data);
    }
  }, [data]);

  return (
    <>
      <Filters
        tagList={[]}
        setTags={() => null}
        setSearch={setSearch}
        state={{ tags: {}, search, take: 100 }}
      />
      <List
        activeColumns={activeColumns}
        columnNumber={7}
        isEmpty={retrievedData.mentorships?.length === 0}
        emptyText="Votre historique est vide"
        loading={!data && !error}
        pages={retrievedData.pages}
        page={page}
        setPage={setPage}
      >
        <OrderTh name="firstName" order={order} setOrder={setOrder}>
          Prénom
        </OrderTh>
        <OrderTh name="lastName" order={order} setOrder={setOrder}>
          Nom
        </OrderTh>
        <Th name="region">Région</Th>
        <OrderTh name="status" order={order} setOrder={setOrder}>
          Statut du mentorat
        </OrderTh>
        <OrderTh name="createdAt" order={order} setOrder={setOrder}>
          Débuté le
        </OrderTh>
        <OrderTh name="updatedAt" order={order} setOrder={setOrder}>
          Terminé le
        </OrderTh>
        <Th name="viewProfile"></Th>
        {retrievedData.mentorships?.map((mentorship, idx) => {
          return (
            <React.Fragment key={idx}>
              <BoldTd name="firstName">
                <Link
                  to={
                    '/mentor/mentorship/management/mentees/details/' +
                    mentorship.id +
                    '/'
                  }
                >
                  {mentorship.mentee?.firstName}
                </Link>
              </BoldTd>
              <BoldTd name="lastName">
                <Link
                  to={
                    '/mentor/mentorship/management/mentees/details/' +
                    mentorship.id +
                    '/'
                  }
                >
                  {mentorship.mentee?.lastName}
                </Link>
              </BoldTd>
              <Td name="region">{mentorship.mentee?.region}</Td>
              <Td name="status">
                {mentorship.status && (
                  <MentorshipStatusChip
                    variant="mentor"
                    status={mentorship.status}
                  />
                )}
              </Td>
              <Td name="createdAt">
                {mentorship.createdAt && getLocaleDate(mentorship.createdAt)}
              </Td>
              <Td name="updatedAt">
                {mentorship.updatedAt && getLocaleDate(mentorship.updatedAt)}
              </Td>

              <Td name="viewProfile">
                <ButtonsContainer>
                  <Link
                    to={
                      '/mentor/mentorship/management/mentees/details/' +
                      mentorship.id +
                      '/'
                    }
                  >
                    <Button color="primary" variant="outlined">
                      Voir le profil
                    </Button>
                  </Link>
                </ButtonsContainer>
              </Td>
            </React.Fragment>
          );
        })}
      </List>
    </>
  );
};

const mapStateToProps = (state: StoreState) => {
  return {
    page: state.mentor.menteeLists.history.page,
    search: state.mentor.menteeLists.history.search,
    order: state.mentor.menteeLists.history.order,
  };
};

const mapDispatchToProps = (dispatch: (action: Action) => void) => {
  return {
    setPage: (page: number) => dispatch(setPage('history', page)),
    setSearch: (search: string) => dispatch(setSearch('history', search)),
    setOrder: (order: OrderValue) => dispatch(setOrder('history', order)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(HistoryList);
